import { Component, type ErrorInfo, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from './ui/button'

function ErrorFallback({ error }: { error: Error | null }) {
  const { t } = useTranslation()

  return (
    <div className="flex h-full items-center justify-center bg-background p-6">
      <div className="w-full max-w-md rounded-lg border border-border bg-surface p-6 text-center shadow-card">
        <h1 className="text-base font-semibold text-foreground">{t('errors:boundary.title')}</h1>
        <p className="mt-2 text-sm text-foreground-muted">{t('errors:boundary.description')}</p>

        {error ? (
          <details className="mt-4 text-start">
            <summary className="cursor-pointer text-xs text-foreground-subtle">
              {t('errors:boundary.details')}
            </summary>
            <pre className="mt-2 overflow-x-auto rounded bg-surface-muted p-2 text-xs text-foreground-muted">
              {error.message}
            </pre>
          </details>
        ) : null}

        <Button className="mt-5 w-full justify-center" onClick={() => window.location.reload()}>
          {t('errors:boundary.action')}
        </Button>
      </div>
    </div>
  )
}

interface State {
  error: Error | null
}

/**
 * Last line of defence. A render error anywhere below this leaves React with an
 * unmounted tree — a white page — which on a fleet console looks exactly like an
 * outage. Catching it keeps the failure legible and recoverable.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Console only for now; phase 5 wires this to the observability stack.
    console.error('[fleetcore] render error', error, info.componentStack)
  }

  override render(): ReactNode {
    if (this.state.error) return <ErrorFallback error={this.state.error} />
    return this.props.children
  }
}
